/* Power BI-style "drop a number, get a total": a number column dragged onto
 * a visual's Values (or picked there) becomes a measure -- "Total Cost" =
 * sum of Cost -- created on the fly through the same measures/ensure endpoint
 * the dashboard suggester uses (one approved aggregation of one real field).
 * An existing measure that already means the same thing is reused. */
import type { QueryClient } from "@tanstack/react-query";

import { apiRequest } from "../api/client";
import { suggestApi } from "../api/v21";

export interface FieldDrag {
  kind: "dimension" | "measure";
  name: string;
  datasetId: string;
  /** A number column (field_role "measure"): summed when it lands in Values. */
  numeric?: boolean;
  label?: string;
  aggregation?: string | null;
  /** Column data type ("text", "number", "date"...) when known, used to pick a chart. */
  dataType?: string;
}

interface MeasureRow { name: string; source_field?: string | null; aggregation?: string | null }

const AGG_WORD: Record<string, string> = { sum: "Total", average: "Average", count: "Count of", count_distinct: "Distinct", minimum: "Lowest", maximum: "Highest" };

export function autoMeasureName(sourceName: string, aggregation = "sum"): string {
  const slug = sourceName.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "value";
  return `${aggregation}_${slug}`.slice(0, 120);
}

/** Returns the measure name to bind for a dropped number column. */
export async function ensureNumberMeasure(qc: QueryClient, mode: "admin" | "owner", f: FieldDrag): Promise<string> {
  const aggregation = f.aggregation || "sum";
  const base = mode === "admin" ? "/api/v1/admin/datasets" : "/api/v1/owner/datasets";
  const key = ["studio-measures", mode, f.datasetId];
  const existing = await qc.fetchQuery({ queryKey: key, queryFn: () => apiRequest<MeasureRow[]>(`${base}/${f.datasetId}/measures`), staleTime: 60_000 });
  const same = existing.find((m) => m.source_field === f.name && (m.aggregation ?? "") === aggregation);
  if (same) return same.name;
  const name = autoMeasureName(f.name, aggregation);
  const label = `${AGG_WORD[aggregation] ?? "Total"} ${f.label ?? f.name}`.replace(/\s+/g, " ").trim();
  await suggestApi.ensureMeasures(f.datasetId, [{ name, display_name: label, source_field: f.name, aggregation }]);
  await qc.invalidateQueries({ queryKey: key });
  return name;
}
